import {
  type BirthDoor,
  birthDoorOpen,
  bornRequested,
  coinedNameOf,
  FIRST_COIN,
  type OriginChoice,
  type OriginsOffer,
  type OriginsReading,
  queueBirth,
} from "../witness/origins";
import type { Hud } from "./hud";
import type { NameAsk, OriginCardView, OriginsView } from "./panels";

/*
 * Where a session enters the world: the lives, the name, and the waking.
 *
 * Phase 6 plan, wave 3C. `hud/opening.ts` is the sentence before this one — the
 * world is built and its history is read — and this is what the fantasy asks for
 * next: *"after the history intro, the player chooses what kind of character to
 * be born as"*, then *"a given name"*
 * (`docs/research/player-fantasies/fantasy-01-born-into-the-world.md` §2–§3).
 *
 * **The owner's ruling of 2026-08-24 makes the last of those three universal.**
 * *"I hate that the prompt for the name comes after spawning — it should be on
 * the starting menus."* So the name is asked here on every path through this
 * file, not only the one that takes a life: a session that declined the lives,
 * declined the birth's own name, was offered none, or asked for `?born=0` is
 * still asked what to call it — before the world is drawn, in the same idiom,
 * with the same two ways out ({@link MENU_ASK}). Nothing is left in the world:
 * the ruling's sharpening settles identity before spawning, so a name given here
 * is put into the record as the session goes live and never mentioned again
 * (`witness/witness.ts`'s `claimTheName`).
 *
 * **Chunk U-4 made the book, the lives and the name one sequence** (plan §5
 * "U-4", `ui-lab/surfaces/opening.html`), and two things went with that:
 *
 * - **the bridge is gone.** A whole screen used to hold the single line *"and
 *   where do you enter it?"*, with a pause on a timer under it, before the lives
 *   arrived. It was a hinge between two surfaces that no longer need one, and a
 *   full screen spent on one question is a screen the sequence then has to
 *   recover from. The question survives verbatim as the lives' own lead
 *   ({@link BRIDGE_QUESTION}), which is where `OriginsView.lead` already put it
 *   once the cards were up;
 * - **the waking arrived.** The last beat is the veil lifting on the street with
 *   the world's first words to the player at the foot of the frame — the
 *   simulation's sentence, one clause per origin door, composed nowhere near
 *   here (`docs/decisions/speech.md`, chunk S-1).
 *
 * It is written as the book's sibling and shares every one of its rules, because
 * they are rules about *when* rather than about what:
 *
 * - **It is skippable at every step.** Escape at the cards, escape or an empty
 *   field at either naming. A session that declines the lives is the Phase 5
 *   first minute in every way but one: it still has a name if it wants one, and
 *   the record takes that name at spawn without asking again (plan decision 4,
 *   *two doors*, executed in full).
 * - **It is gated on the URL alone** (`witness/origins.ts`'s `bornRequested`),
 *   never on `import.meta.env.DEV` — O7's rule. `?born=0` is the same session
 *   without the *lives* — it still names itself at the menu, since the ruling
 *   above — which is what makes being born against claiming one playtest instead
 *   of two builds, and now isolates the life rather than the name.
 * - **It runs before the loop and before the controls exist**, which is what
 *   makes the keyboard handling below safe to write at all: nothing else is
 *   listening yet, so enter cannot also be a jump and escape cannot also drop a
 *   pointer lock that has never been taken (`main.ts` constructs `Controls`
 *   after this resolves). The one exception is the *field*, which takes the
 *   keyboard the moment it is up — a digit typed into a name is part of the name
 *   ({@link onKeyIsForTheField}).
 * - **Nothing on the screen is composed here.** The lives, their towns, their
 *   parents, the seats they were born beside and the sentence the world wakes
 *   them with are the simulation's; `witness/origins.ts` assembles the card's
 *   line out of the source's own nouns and this file decides which card is
 *   centred.
 *
 * The one thing it adds to the book's list is a refusal: **a build that cannot
 * record a birth does not offer one.** The read and the door are two exports and
 * a boundary halfway through a wave may have grown one and not the other, so the
 * screen asks for both before it opens ({@link chooseAnOrigin}).
 */

/**
 * The question the lives are offered under.
 *
 * §4's fiction — birth and claim are two doors, and this is the hinge between the
 * world's story and the player's place in it. It was a *screen* until chunk U-4
 * and it is a *lead* now: the book closes, the lives arrive, and the question is
 * said quietly over them rather than held on a frame of its own for three and a
 * half seconds.
 *
 * "It" is the history they have just been reading, and the sentence deliberately
 * does not say so — naming it again would be the client explaining its own
 * transition.
 */
const BRIDGE_QUESTION = "and where do you enter it?";

/**
 * The foot line at the lives: how to move, how to choose, and how to leave.
 *
 * ← → rather than ↑ ↓ since chunk U-4, and it is the only word of it that
 * changed: the lives are a strip read sideways now, and a foot line naming a
 * gesture the screen does not answer is worse than no foot line at all. Both
 * pairs still move the cursor ({@link moveFor}) — what the hint names is the
 * one a reader should reach for.
 */
const CARDS_HINT = "← → choose a life · enter takes it · escape begins without one";

/**
 * What a card says where the record says nothing about a birth.
 *
 * **It is an absence rather than a composition**, which is the only reason this
 * client is allowed to type it. Two of the four doors are *defined* by having no
 * birth on record — an orphan is *"an orphan of ruined X"* and an unrecorded
 * child is *"a child no chronicle recorded"* — and the simulation says the same
 * thing about both in its own register at the beat after this one: *"Your birth
 * is in no chronicle"*, *"Nobody wrote the day of your birth down"*
 * (`docs/decisions/speech.md`, chunk S-1's `waking/unwritten`). So the row is a
 * label about the door, in the family of *"the pages are blank"* and *"no
 * likeness recorded"*, and not a sentence about a record.
 *
 * The other half of the lab's card — *born year 152* on a life the chronicles
 * did write down — is **not** drawn: no export anywhere carries a figure's birth
 * year, so the row is left out rather than guessed at. Filed with the seam named
 * (`docs/decisions/ui.md` §5).
 */
const NO_BIRTH_RECORD = "no record of a birth";

/**
 * What the *coin another* button says.
 *
 * Plain, and it says what it does rather than what it is: a player pressing it
 * is asking the world for another name, not setting a nonce. The standing rule
 * for every string this client shows is that a word somebody has to look up is a
 * prompt that failed (`witness/witness.ts`'s labels).
 */
const COIN_AGAIN = "coin another name";

/**
 * What the name card says when it is a *birth* being named rather than a session.
 *
 * The birth screen's own prose through Phase 5's card (`hud/panels.ts`'s
 * {@link NameAsk}), and the difference between the two askings is the whole of
 * decision 4: this one is asked of a life about to start and the other of
 * somebody already standing in the world.
 *
 * Silent about houses **in its prose**, and since Phase 7 not silent about them
 * on the card: a life whose line the world would coin shows what it would write
 * down and offers another draw ({@link birthAsk}). That is the surname ruling of
 * 2026-08-25 answering the open question this paragraph used to name — *"the sim
 * owns surnames, permanently"*, and what the player chooses is which of its
 * draws to live with. A bound origin inherits the name of the line it was born
 * into and no press can rename it (decision 5, inherit-or-coin), so it gets no
 * button at all. A card that apologised for it would be answering the question
 * in the briefing.
 */
const BIRTH_ASK: NameAsk = {
  title: "and what were you called?",
  said: "Say a name and the world will write it down. Leave it blank and this life is not yours.",
  confirm: "that was my name",
  decline: "not this life",
};

/**
 * What the card says when no life was taken and the session still wants a name.
 *
 * The menu naming step, which the owner's ruling of 2026-08-24 added to every
 * other path through this file. **Declining a life is not declining a name**:
 * *"leave it blank and this life is not yours"* is a sentence about the birth at
 * the wall ({@link BIRTH_ASK}), and a player who read it and walked away should
 * not have lost the ability to be called anything.
 *
 * The words are Phase 5's claim card, moved rather than rewritten — *"Say a name
 * and the world will have one for you. Leave it blank and nobody will use one"*
 * is the sentence the last two prototypes were played on, and it is still exactly
 * true. What changed is *where* it is read, which is the whole of the ruling.
 *
 * Only the question above it is new, and it is the one this moment can honestly
 * ask. Phase 5's *"what are you called?"* was put to somebody standing in a town;
 * nobody is standing anywhere yet, so it asks about the chronicles instead — the
 * one thing this client can promise a name is *for*, and the thing the whole
 * start flow has just spent three pages being about.
 */
const MENU_ASK: NameAsk = {
  title: "and if the chronicles ever ask — your name?",
  said: "Say a name and the world will have one for you. Leave it blank and nobody will use one.",
  confirm: "that is my name",
  decline: "no name",
};

/**
 * How a session began, for a driver and for the record.
 *
 * Five outcomes rather than a boolean because they are five different sessions,
 * and the middle three are the ones a self-check has to be able to tell apart: a
 * screen that never opened because the link declined it, a screen that never
 * opened because this build cannot record a birth, and a screen that never opened
 * because the world had no life to offer are three separate facts about three
 * separate builds ({@link OpeningRead} makes the same split for the book).
 */
export type BirthRead = "declined" | "unoffered" | "closed" | "refused" | "born";

/** What the birth screen settled on, and what was done about it. */
export interface BornSession {
  readonly read: BirthRead;

  /** The life that was taken, or `null` in every session nobody was born in. */
  readonly chosen: OriginChoice | null;

  /**
   * What the session is called, from whichever card asked.
   *
   * The birth's name when a life was taken and the menu naming step's when one
   * was not — one field rather than two, because there is one name and the
   * *card* it was typed on is not a fact anything downstream needs
   * ({@link BornSession.chosen} already says which door this was). Empty is the
   * real answer for a session that skipped: an unnamed player is simply never
   * cited, and `main.ts` claims nothing for them.
   */
  readonly name: string;
}

/**
 * The read the waking sentence comes through, if this build has one.
 *
 * `WitnessSource.wakingLine` (chunk S-1's export, wave U2's boundary), asked for
 * by the **row index into `origins()`** and answered with the whole sentence.
 * Optional and presence-checked for `OriginsSurface`'s reason: a boundary
 * halfway through a wave may have grown the cards and not the sentence, and a
 * beat with no sentence is a beat that says nothing rather than a beat that
 * throws.
 */
export interface WakingSurface {
  wakingLine?(origin: number, name: string): string;
}

/**
 * What the waking is read out of, which is **the same source the cards came
 * from**.
 *
 * `OriginsReading` with one more read on it, and the intersection is the point
 * rather than an economy: `wakingLine` names a row of `origins()`' own list, so
 * asking one source about a card another source listed would describe a life
 * nobody was offered. The hand-authored stand-in behind `?bornfake=1` carries no
 * `wakingLine` at all (`dev/birthfake.ts`), which is why a fake session says
 * nothing here and does not have to be told to.
 */
export type WakingReading = OriginsReading & Partial<WakingSurface>;

/**
 * Puts the lives up, then the name, then the waking, and resolves when the
 * session knows who it is.
 *
 * The second awaited call in `main.ts`'s boot sequence, straight after the book
 * closes and before the controls are armed. **Everything asynchronous about it is
 * the wait for a person**: the lives are read before it is called and nothing
 * here touches the simulation until the last line, so no frame is blocked and the
 * page the book was on is still painted when the lives replace it.
 *
 * The birth is queued *here* rather than handed back for `main.ts` to queue,
 * which is the one thing in this file that is not presentation. It is deliberate:
 * the command and the screen that chose it are one decision, and a caller holding
 * a card and a name has every opportunity to queue a life the player declined.
 *
 * **Every path out of here goes through a name** ({@link nameless}), which is the
 * owner's ruling: the two ways the screen never opens and the two ways a player
 * leaves it all ask at the menu instead. The one exception is a session with
 * nothing to witness — see `witnessed` below, and it is a fact about the world
 * rather than a preference.
 *
 * **And every path out of here goes through the waking**, which is the sequence's
 * last beat: the veil lifts, and where a life was taken the world says its first
 * words over the street ({@link OriginsPanel.lift}). It does not block — the
 * frame loop has not started yet and the beat plays out over the first seconds of
 * the session, which is the only way the veil can lift onto anything at all.
 */
export async function chooseAnOrigin(
  hud: Hud,
  offer: OriginsOffer,
  door: BirthDoor,
  search: string,
  /**
   * Whether this session has anywhere for a name to land.
   *
   * `main.ts` passes whether it built a witnessing layer at all. A world with no
   * history (`?bootstrap=0`) has no towns, no records and nothing that could ever
   * cite a player, so there is no claim to send there either — and a client that
   * collected a string it had already decided to do nothing with would be asking a
   * question for the look of it. It is the same answer the book gives that world:
   * nothing, said quietly.
   */
  witnessed: boolean,

  /**
   * What stands over the lives, or `null` for the question the book closes on.
   *
   * **The death screen is this screen's own bridge** (Phase 7, wave 2D-a,
   * decision 7). A session whose figure has been killed comes back here over the
   * world that remembers, and what it comes back *through* is one sentence: the
   * log's own words about the killing, composed by the simulation and passed
   * straight in ({@link Witnessing.epitaph}). It is the beat's lead, in the same
   * register the question is said in — the lives are under it either way, with
   * the late figure's line among them.
   *
   * A parameter rather than a second screen because it is the same moment asked
   * twice — *and where do you enter it?* — and a second screen would be a second
   * place for the cards, the keys and the naming to drift.
   */
  lead: string | null = null,

  /**
   * The source the waking sentence is asked of, or `null` for a build without
   * one.
   *
   * **It must be the source the `offer` was read out of** ({@link WakingReading}).
   * `null` — and a source with no `wakingLine` — is a beat that lifts the veil
   * and says nothing, which is exactly what a fixture and a hand-authored stand-in
   * should do.
   */
  waking: WakingReading | null = null,
): Promise<BornSession> {
  if (!bornRequested(search)) {
    return nameless(hud, "declined", witnessed);
  }

  if (offer.cards.length === 0 || !birthDoorOpen(door)) {
    // Nothing is said about it. A world with no life to offer and a build whose
    // boundary has not grown the door both start the session they would have
    // started anyway, which is the same answer this client gives to a world with
    // no ages in it (`hud/opening.ts`) — and both are still asked for a name,
    // because that is a question about the session rather than about the lives.
    return nameless(hud, "unoffered", witnessed);
  }

  // The loading prompt is what is on screen when there was no book to replace it
  // (`hud/opening.ts` clears it only when it puts a page up), and this screen is
  // read against a darkened frame rather than through *"building the world"*. A
  // screenshot is what found it: the fixture has no ages and no lives, so until
  // the ruling of 2026-08-24 nothing was ever drawn here to show the prompt still
  // standing over it. `main.ts` puts the play prompt up after this resolves.
  hud.setOverlay(null);

  const picked = await pickALife(hud, offer, lead ?? BRIDGE_QUESTION);

  if (picked === null) {
    hud.origins.hide();

    return nameless(hud, "closed", witnessed);
  }

  // **The lives step aside rather than being dimmed behind the card** (chunk
  // U-4). The chosen one travels into the naming beat and sits beside the
  // question it raised (`hud/panels/name.ts`), so leaving the strip up behind it
  // would draw the same life twice. What stays is the veil, which is the thing
  // the start flow is read against from the book's first page to the last
  // keystroke of a name — and which the naming beat has never had one of its
  // own.
  hud.origins.veil();

  // **The coin nonce lives here, for the life of this asking.** It is the one
  // piece of state the birth screen keeps that is not a card, and it belongs to
  // the *screen* rather than to the session: a player who presses *coin another*
  // three times and then declines the life has pressed nothing at all, because
  // the nonce goes with the card.
  const coined = { nonce: FIRST_COIN, line: coinedNameOf(door, picked.card, "", FIRST_COIN) };
  const name = await askTheName(hud, birthAsk(hud, door, picked, coined));

  if (name === null) {
    // Declining the name declines the *life*: a birth with no name is not one
    // this client will queue, because the name is what the command carries and a
    // blank one is a silent refusal at the wall anyway (`history::player`'s
    // `born`). Said plainly on the card — *"leave it blank and this life is not
    // yours"* — so it is a choice rather than a dead end.
    //
    // **And then the menu asks anyway**, which is the ruling's finest point and
    // worth saying out loud: declining a *life* is not declining a *name*. The
    // player is offered the plainer question a beat later and may decline that
    // too ({@link MENU_ASK}).
    return nameless(hud, "closed", witnessed);
  }

  const queued = queueBirth(door, picked.card, name, coined.nonce);

  // And the last beat: the veil goes, and the world says its first words to
  // whoever is standing in the street. The sentence is asked for with the name
  // that was just typed, because that is the name the record will carry
  // ({@link wakingSaid}).
  hud.origins.lift(wakingSaid(waking, offer, picked.at, name));

  return { read: queued ? "born" : "refused", chosen: picked.card, name };
}

/**
 * The world's first words to this session, or the empty string.
 *
 * **Exported for the self-check**, which is the one thing in this file that is:
 * the second refusal below is the highest-consequence line in the beat and the
 * flow around it resolves on two gestures, so a check that could only reach it
 * through the promises could not reach it at all
 * (`dev/selfcheck/figure.ts`'s note on gesture-resolved paths).
 *
 * Three refusals, and each is a fact rather than a taste:
 *
 * - **no read, no sentence.** The export is presence-checked, like every other
 *   one this file crosses (`witness/origins.ts`'s `BirthDoor`);
 * - **a list with anything refused out of it is not asked about.** The export
 *   takes the row index into `origins()` and what this client holds is the index
 *   into the cards that *survived* the read — the same list only while
 *   `refused.cards` is zero, which `witness/origins.ts` already calls a defect
 *   rather than a taste. A shifted index would not fail: it would describe a
 *   different life in perfect prose, which is the worst failure a sentence can
 *   have;
 * - **a row past the end is the empty string**, which is the wall's own
 *   convention and what the fixture answers with for every row
 *   (`world/fixture.ts`).
 */
export function wakingSaid(
  source: WakingReading | null,
  offer: OriginsOffer,
  at: number,
  name: string,
): string {
  const ask = source?.wakingLine;

  if (source === null || typeof ask !== "function") {
    return "";
  }

  if (offer.refused.cards !== 0 || at < 0) {
    return "";
  }

  return ask.call(source, at, name);
}

/**
 * The birth's naming card: the life it is about, and the coined family name
 * where there is one.
 *
 * **The card travels** (chunk U-4). The naming beat draws the life that was just
 * chosen beside the question, so the view carries it rather than the screen
 * behind it holding it up — which is what lets the lives step aside without the
 * player losing sight of what they are naming.
 *
 * **The coin button is drawn only where the world would coin something** — which
 * is the surname ruling's own rule read off the simulation rather than restated
 * here: a life that inherits a line answers with the empty string at every press,
 * because no press can rename a house (`witness/origins.ts`'s `coinedNameOf`).
 * So an orphan gets the button, a child of housed parents does not, and neither
 * case is a branch this file writes.
 *
 * The preview is asked with an **empty name**, and that is deliberate: the
 * family name a birth coins is drawn from a stream keyed on the founder and the
 * press and nothing else (`sim_core::history::house`), so what the player types
 * cannot change it — and asking again on every keystroke would be a clone of the
 * world per letter.
 */
function birthAsk(
  hud: Hud,
  door: BirthDoor,
  picked: PickedLife,
  coined: { nonce: number; line: string },
): NameAsk {
  const ask: NameAsk = { ...BIRTH_ASK, card: cardOf(picked.card) };

  if (coined.line === "") {
    return ask;
  }

  const again = (): void => {
    coined.nonce += 1;

    const drawn = coinedNameOf(door, picked.card, "", coined.nonce);

    // A press that draws nothing leaves the last line standing rather than
    // blanking the card: the nonce has no last page (`Command::Born`'s own
    // note), so an empty answer here means a build that stopped previewing
    // rather than a world that ran out of names.
    if (drawn !== "") {
      coined.line = drawn;
    }

    hud.name.recoin({ line: coinedLine(coined.line), again: COIN_AGAIN, onAgain: again });
  };

  return {
    ...ask,
    coined: { line: coinedLine(coined.line), again: COIN_AGAIN, onAgain: again },
  };
}

/** How the coined line reads on the card: the world's word, and whose it is. */
function coinedLine(house: string): string {
  return `the world writes your line down as ${house}`;
}

/**
 * One life as the screen draws it: what the seam said, and what it did not.
 *
 * The whole of the client's part in a card, and it is two clauses long: the door
 * decides whether there is a birth to name ({@link NO_BIRTH_RECORD}) and
 * everything else is forwarded. The line and the claims are already assembled
 * from the simulation's own nouns a file away, and the citation is the record the
 * offer stands on.
 */
function cardOf(choice: OriginChoice): OriginCardView {
  return {
    line: choice.line,
    born: choice.kind === "orphan" || choice.kind === "unrecorded" ? NO_BIRTH_RECORD : "",
    claims: choice.claims.map((claim) => claim.line),
    cites: choice.cites,
  };
}

/**
 * Nobody was born: the shape every outcome but one answers with, and the name it
 * is asked for anyway.
 *
 * The owner's ruling of 2026-08-24 in one function. Five different sessions reach
 * it — the link declined the screen, the build could not offer one, the world had
 * no life in it, the player left at the lives, the player declined the birth's own
 * name — and all five are the same question from here: *what shall the chronicles
 * call you*, asked at the menu, before the world is drawn, and skippable.
 *
 * The lives are gone by the time this runs (the caller takes them down), so the
 * card is put up against the bare darkened frame instead
 * ({@link OriginsPanel.veil}) — the start flow is read against **one** dark frame
 * from the book's first page to the last keystroke of a name, and a card floating
 * over an undrawn world with the session's chrome showing through it is the
 * regression a wave-3C screenshot caught once already.
 *
 * It ends on the same beat every other path does: the veil lifts, and nothing is
 * said over it. A session that took no life has no row for the world to have
 * words about, which is the empty string the export answers with anyway
 * (`world/types.ts`'s `wakingLine`) — so the silence here is the boundary's own
 * and not this file's manners.
 */
async function nameless(hud: Hud, read: BirthRead, witnessed: boolean): Promise<BornSession> {
  if (!witnessed) {
    return { read, chosen: null, name: "" };
  }

  // Same reason as the screen above: what is behind this card is a darkened
  // frame, not the sentence the client was showing while it built the world.
  hud.setOverlay(null);
  hud.origins.veil();

  const name = await askTheName(hud, MENU_ASK);

  hud.origins.lift("");

  // A decline is a real answer and is answered with silence: the session is
  // unnamed, no claim is ever sent, and nothing in the world ever cites the
  // player (Phase 5 decision 1, `witness/witness.ts`).
  return { read, chosen: null, name: name ?? "" };
}

/** A life and where it sat in the list, which is what the waking is asked by. */
interface PickedLife {
  readonly card: OriginChoice;

  /**
   * Its row in {@link OriginsOffer.cards}.
   *
   * Carried rather than looked up afterwards, because two identical cards would
   * make `indexOf` answer about the first of them — and the index is what the
   * waking sentence is keyed on ({@link wakingSaid}).
   */
  readonly at: number;
}

/**
 * Draws the lives and resolves with the one that was taken.
 *
 * The listener is on `document` rather than on the panel for {@link openTheBook}'s
 * reason: the panel is not focusable and a menu of lives is not a form, so what a
 * reader presses should work wherever the cursor happens to be. It is removed the
 * moment the beat closes, so nothing this file bound survives into the session
 * that follows — which matters here for the same reason it matters there, since
 * `Controls` binds the same keys on the same target a few lines later.
 */
async function pickALife(
  hud: Hud,
  offer: OriginsOffer,
  lead: string,
): Promise<PickedLife | null> {
  const cards: readonly OriginCardView[] = offer.cards.map(cardOf);

  return new Promise<PickedLife | null>((resolve) => {
    let cursor = 0;
    let done = false;

    const settle = (at: number | null): void => {
      if (done) {
        return;
      }

      done = true;
      document.removeEventListener("keydown", onKey);

      const card = at === null ? undefined : offer.cards[at];

      // The panel is deliberately left on the page: whether it comes down here or
      // after the name is asked is {@link chooseAnOrigin}'s call, and it has the
      // reason. What is settled here is the listener, so nothing this function
      // bound survives the choice.
      resolve(at === null || card === undefined ? null : { card, at });
    };

    const draw = (): void => {
      hud.origins.show({
        lead,
        cards,
        cursor,
        hint: CARDS_HINT,
        onChoose: (card: number) => {
          settle(card);
        },
        onCursor: (card: number) => {
          cursor = Math.min(Math.max(card, 0), cards.length - 1);
          draw();
        },
        onDecline: () => {
          settle(null);
        },
      } satisfies OriginsView);
    };

    const onKey = (event: KeyboardEvent): void => {
      if (onKeyIsForTheField(event)) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        settle(null);

        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        settle(cursor);

        return;
      }

      const step = moveFor(event.key);

      if (step === 0) {
        return;
      }

      event.preventDefault();

      // Clamped rather than wrapped, for the reason a turn off the end of the
      // book is nothing: the card somebody is on is the card they meant to be on,
      // and a strip that jumped from the last life to the first would move the
      // selection furthest exactly when a player pressed a key expecting it not
      // to move at all.
      cursor = Math.min(Math.max(cursor + step, 0), cards.length - 1);
      draw();
    };

    document.addEventListener("keydown", onKey);
    draw();
  });
}

/**
 * Asks for a name through Phase 5's card, in whichever of the start menus' two
 * wordings this asking is.
 *
 * `null` is a decline, and it is a real answer: blank, escape, or the second
 * button. What the wall does with what is typed is the simulation's business —
 * whitespace, control characters, the ends and the byte cap are normalized there
 * and nowhere else, because the replay format has to agree with exactly one
 * normalization (`world/types.ts`).
 *
 * One function for both askings rather than two, because the difference between
 * them is four strings and a card and the sameness is everything else: the field,
 * the byte cut, the two keys and the meaning of a blank answer.
 */
async function askTheName(hud: Hud, ask: NameAsk): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    hud.name.ask((name: string | null) => {
      resolve(name);
    }, ask);
  });
}

/**
 * Whether a key belongs to the one text field in this client rather than to the
 * flow around it.
 *
 * **A digit or a letter typed into a name is part of the name** — the lab's rule
 * (`ui-lab/surfaces/opening.html`), and the second of the two guards that keep
 * it: the field stops its own key events from ever reaching `document`
 * (`hud/panels/name.ts`), and this returns on the ones that somehow do. It is
 * worth having both because the digits stop being free in this same wave — the
 * hotbar takes 1–0 (chunk U-5) — and a name with a `3` in it must not arm a
 * weapon.
 *
 * Exported to nobody: both handlers that need it are in this flow
 * (`hud/opening.ts` states the same rule against its own listener).
 */
function onKeyIsForTheField(event: KeyboardEvent): boolean {
  return event.target instanceof HTMLInputElement;
}

/**
 * Which way a key moves the cursor: along the strip, back, or not at all.
 *
 * By `key` rather than by `code`, unlike everything in `input/controls.ts`, for
 * the reason `hud/opening.ts` gives: that file binds *letters* to verbs and has to
 * survive a layout, and these are the keys that already mean "next" and
 * "previous" on every keyboard whatever is printed on them.
 *
 * **Tab is on the list and stays on it**: it is how the death screen's heir cards
 * are walked, which is why `main.ts` drains the menu's own toggle count while a
 * mourning session is up (chunk U-3, `Controls.takeMenuToggles`).
 */
function moveFor(key: string): number {
  if (key === "ArrowRight" || key === "ArrowDown" || key === "PageDown" || key === "Tab") {
    return 1;
  }

  if (key === "ArrowLeft" || key === "ArrowUp" || key === "PageUp") {
    return -1;
  }

  return 0;
}
